import { Note, Person, Settings } from '@mui/icons-material';
import ProjectNavigation from './ProjectNavigation/ProjectNavigation';
import RightSidebar from './RightSidebar/RightSidebar';
import NotesModeBody from './RightSidebar/modes/NotesModeBody';
import CharactersModeBody from './RightSidebar/modes/CharactersModeBody';
import ProjectSettingsModeBody from './RightSidebar/modes/ProjectSettingsModeBody';
import { getProjectTypeConfig } from '../../../utils/projectTypeConfig';

/**
 * Sidebar configuration based on routes
 * Each route can specify:
 * - leftSidebar: Component to render in left sidebar (or null to hide)
 * - rightSidebar: Component to render in right sidebar (or null to hide)
 * - leftSidebarProps: Props to pass to left sidebar component
 * - rightSidebarProps: Props to pass to right sidebar component
 */

/**
 * Get the route pattern from a pathname
 * Converts /projectDetails/123/characters to /projectDetails/:projectId/characters
 */
export const getRoutePattern = (pathname) => {
  // Replace project IDs with :projectId
  let pattern = pathname.replace(/\/projectDetails\/[^/]+/g, '/projectDetails/:projectId');

  // Replace corkboard IDs with :corkboardId
  pattern = pattern.replace(/\/corkboards\/[^/]+/g, '/corkboards/:corkboardId');

  // Replace timeline IDs with :timelineId
  pattern = pattern.replace(/\/timelines\/[^/]+/g, '/timelines/:timelineId');

  // Replace game design tool with :gameTool
  pattern = pattern.replace(/\/gamedesign\/[^/]+/g, '/gamedesign/:gameTool');

  return pattern;
};

/**
 * Match a pathname against a route pattern
 */
export const matchRoute = (pathname, pattern) => {
  const routePattern = getRoutePattern(pathname);

  // Exact match
  if (routePattern === pattern) {
    return true;
  }

  // Pattern matching (for more complex matching if needed)
  // Escape special regex characters and replace :param with [^/]+
  const escapedPattern = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/:[^/]+/g, '[^/]+');
  const patternRegex = new RegExp(`^${escapedPattern}$`);
  return patternRegex.test(routePattern);
};

/**
 * Helper function to create left sidebar props for ProjectNavigation sidebar
 * All pages use the same ProjectNavigation sidebar
 */
const createLeftSidebarProps = (props, activeSection = null) => ({
  activeProject: props.activeProject,
  chapters: props.chapters,
  characters: props.characters || [],
  notes: props.notes || [],
  handleAddChapter: props.handleAddChapter,
  handleChapterClick: props.handleChapterClick,
  handleAddScene: props.handleAddScene,
  handleSceneClick: props.handleSceneClick,
  setRefreshScenesCallback: props.setRefreshScenesCallback,
  onToggle: props.toggleLeftSidebar,
  onExport: props.handleExportProject,
  onImport: props.handleImportDocument,
  onShare: props.handleShareProject,
  activeSection: activeSection || props.activeCenterSection || 'manuscript',
  onSectionChange: props.onCenterSectionChange,
  onGameToolSelect: props.onGameToolChange,
  activeGameTool: props.gameTool || 'erd',
});

/**
 * Create the modes array for the right sidebar.
 * Modes that don't apply to the project type (e.g. Characters for poetry/essay)
 * are filtered out.
 */
const createRightSidebarModes = (projectType) => {
  const typeConfig = getProjectTypeConfig(projectType);
  return [
    {
      id: 'notes',
      label: 'Notes',
      icon: Note,
      barBodyComponent: NotesModeBody,
    },
    ...(typeConfig.showCharacters
      ? [
          {
            id: 'characters',
            label: 'Characters',
            icon: Person,
            barBodyComponent: CharactersModeBody,
          },
        ]
      : []),
    {
      id: 'settings',
      label: 'Project Settings',
      icon: Settings,
      barBodyComponent: ProjectSettingsModeBody,
    },
  ];
};

/**
 * Helper function to create right sidebar props
 */
const createRightSidebarProps = (props) => ({
  modes: createRightSidebarModes(props.activeProject?.type),
  defaultMode: 'settings', // Default to project settings
  projectId: props.activeProject?._id || null,
  chapters: props.chapters,
  notes: props.notes,
  characters: props.characters,
  families: props.families || [], // Families might not be available
  collaborators: props.collaborators,
  activeProject: props.activeProject,
  formatDate: props.formatDate,
  handleAddChapter: props.handleAddChapter,
  handleAddNote: props.handleAddNote,
  handleEditProject: props.handleEditProject,
  handleAddCharacter: props.handleAddCharacter,
  handleExportProject: props.handleExportProject,
  handleImportDocument: props.handleImportDocument,
  onCharacterClick: props.onCharacterClick,
  onToggle: props.toggleRightSidebar,
});

/**
 * Sidebar configuration map
 * Keys are route patterns, values are configuration objects
 * All routes use the same ProjectNavigation sidebar for the left sidebar
 */
export const sidebarConfig = {
  // Default manuscript route
  '/projectDetails/:projectId': {
    leftSidebar: ProjectNavigation,
    rightSidebar: RightSidebar,
    leftSidebarProps: (props) => createLeftSidebarProps(props, 'manuscript'),
    rightSidebarProps: createRightSidebarProps,
  },

  // Characters route
  '/projectDetails/:projectId/characters': {
    leftSidebar: ProjectNavigation,
    rightSidebar: RightSidebar,
    leftSidebarProps: (props) => createLeftSidebarProps(props, 'characters'),
    rightSidebarProps: createRightSidebarProps,
  },

  // Notes routes
  '/projectDetails/:projectId/notes': {
    leftSidebar: ProjectNavigation,
    rightSidebar: RightSidebar,
    leftSidebarProps: (props) => createLeftSidebarProps(props, 'notes'),
    rightSidebarProps: createRightSidebarProps,
  },

  // Corkboards list
  '/projectDetails/:projectId/notes/corkboards': {
    leftSidebar: ProjectNavigation,
    rightSidebar: RightSidebar,
    leftSidebarProps: (props) => createLeftSidebarProps(props, 'corkboards'),
    rightSidebarProps: createRightSidebarProps,
  },

  // Corkboard editor
  '/projectDetails/:projectId/notes/corkboards/:corkboardId': {
    leftSidebar: ProjectNavigation,
    rightSidebar: RightSidebar,
    leftSidebarProps: (props) => createLeftSidebarProps(props, 'corkboards'),
    rightSidebarProps: createRightSidebarProps,
  },

  // Timelines list
  '/projectDetails/:projectId/notes/timelines': {
    leftSidebar: ProjectNavigation,
    rightSidebar: RightSidebar,
    leftSidebarProps: (props) => createLeftSidebarProps(props, 'timelines'),
    rightSidebarProps: createRightSidebarProps,
  },

  // Timeline editor
  '/projectDetails/:projectId/notes/timelines/:timelineId': {
    leftSidebar: ProjectNavigation,
    rightSidebar: RightSidebar,
    leftSidebarProps: (props) => createLeftSidebarProps(props, 'timelines'),
    rightSidebarProps: createRightSidebarProps,
  },

  // Game design (video game projects)
  '/projectDetails/:projectId/gamedesign': {
    leftSidebar: ProjectNavigation,
    rightSidebar: RightSidebar,
    leftSidebarProps: (props) => createLeftSidebarProps(props, 'gamedesign'),
    rightSidebarProps: createRightSidebarProps,
  },

  // Game design tool (erd | types | formulas)
  '/projectDetails/:projectId/gamedesign/:gameTool': {
    leftSidebar: ProjectNavigation,
    rightSidebar: RightSidebar,
    leftSidebarProps: (props) => createLeftSidebarProps(props, 'gamedesign'),
    rightSidebarProps: createRightSidebarProps,
  },
};

/**
 * Get sidebar configuration for a given pathname
 * Returns the most specific matching route configuration
 */
export const getSidebarConfig = (pathname) => {
  const routePattern = getRoutePattern(pathname);

  // Try exact match first
  if (sidebarConfig[routePattern]) {
    return sidebarConfig[routePattern];
  }

  // Try to find the most specific matching route
  // Sort by specificity (longer patterns first)
  const patterns = Object.keys(sidebarConfig).sort((a, b) => b.length - a.length);

  const matchingPattern = patterns.find((pattern) => matchRoute(pathname, pattern));
  if (matchingPattern) {
    return sidebarConfig[matchingPattern];
  }

  // Default to manuscript route
  return sidebarConfig['/projectDetails/:projectId'];
};
